<template>
  <div>
    <div class="mt-6">
      <StoreGeneralInformation :store="store" @storeUpdated="emitStoreUpdated" />
    </div>

    <TabsNavigation :tabs="tabsWithBadge" class="my-6" />

    <div v-if="currentTab === 'general'" class="mt-6">
      <StoreConfigurationGeneralInformations
        :store="store"
        :is-store-setup="false"
        @validate="validateConfigurationGeneralInformations"
        @reject="rejectConfigurationGeneralInformations"
        @updated="emitStoreUpdated"
      />
    </div>

    <div v-if="currentTab === 'contactInformations'" class="mt-6">
      <StoreConfigurationContactInformations
        :store="store"
        :is-store-setup="false"
        @validate="validateConfigurationContactInformations"
        @reject="rejectConfigurationContactInformations"
        @updated="emitStoreUpdated"
      />
    </div>

    <div v-if="currentTab === 'afterSalesService'" class="mt-6">
      <StoreConfigurationAfterSalesService
        :store="store"
        :is-store-setup="false"
        @validate="validateConfigurationAfterSalesService"
        @reject="rejectConfigurationAfterSalesService"
        @updated="emitStoreUpdated"
      />
    </div>

    <div v-if="currentTab === 'billingInformations'" class="mt-6">
      <StoreBillingInformation :store="store" :is-store-setup="false" @remunerationModelUpdated="emitStoreUpdated" />
    </div>

    <div v-if="currentTab === 'businessVerticals'" class="mt-6">
      <StoreBusinessVerticals :store="store" />
    </div>

    <div v-if="currentTab === 'discount'" class="mt-6">
      <StoreDiscountInformation
        :store="store"
        @discountUpdated="emitStoreUpdated"
        @challengeUpdated="emitStoreUpdated"
      />
    </div>

    <div v-if="currentTab === 'activity'" class="mt-6">
      <StoreEventNotes :store="store" @addedNote="emitStoreUpdated" />
    </div>

    <div v-if="currentTab === 'users'" class="mt-6">
      <MerchantProfilesList
        :organizationIds="store.organization?.id ? [store.organization.id] : []"
        :storeIds="storeId ? [storeId] : []"
      />
    </div>

    <div v-if="currentTab === 'sellers' && isStoreOffline" class="mt-6">
      <SellersInformation :sellers="store.sellers" @getSellers="emitStoreUpdated" @deleteSeller="deleteSeller" />
    </div>

    <div v-if="currentTab === 'offers'" class="mt-6">
      <StoreMatchedContracts
        :matched-contracts="matchedContractsWithFees"
        :store-id="storeId"
        :sales-channel="store.salesChannel"
      />
    </div>
  </div>
</template>

<script>
import StoreGeneralInformation from "@/components/store/details/StoreGeneralInformation.vue";
import StoreBusinessVerticals from "@/components/store/details/StoreBusinessVerticals.vue";
import StoreMatchedContracts from "@/components/store/details/StoreMatchedContracts.vue";
import StoreDiscountInformation from "@/components/store/details/StoreDiscountInformation.vue";
import StoreBillingInformation from "@/components/store/details/StoreBillingInformation.vue";
import SellersInformation from "@/components/store/details/SellersInformation.vue";
import StoreConfigurationGeneralInformations from "@/components/store/details/StoreConfigurationGeneralInformations.vue";
import StoreConfigurationContactInformations from "@/components/store/details/StoreConfigurationContactInformations.vue";
import StoreConfigurationAfterSalesService from "@/components/store/details/StoreConfigurationAfterSalesService.vue";
import MerchantProfilesList from "@/components/merchant_profiles/MerchantProfilesList.vue";
import StoreEventNotes from "@/components/store/details/StoreEventNotes.vue";

import { TabsNavigation } from "@estaly/ui";

import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "StoreActive",
  components: {
    StoreGeneralInformation,
    StoreBusinessVerticals,
    StoreMatchedContracts,
    StoreDiscountInformation,
    StoreBillingInformation,
    SellersInformation,
    StoreConfigurationGeneralInformations,
    StoreConfigurationContactInformations,
    StoreConfigurationAfterSalesService,
    MerchantProfilesList,
    StoreEventNotes,
    TabsNavigation,
  },
  props: {
    store: { type: Object, required: true },
    storeId: { type: Number, required: true },
  },
  emits: ["store-updated"],
  computed: {
    ...mapGetters("stores", {
      isStoreOfflineGetter: "isStoreOffline",
    }),
    ...mapState("matchedContracts", { matchedContracts: "index" }),
    ...mapGetters("matchedContracts", ["withFeeAmounts"]),

    matchedContractsWithFees() {
      return this.matchedContracts.map(this.withFeeAmounts);
    },
    isStoreOffline() {
      return this.isStoreOfflineGetter(this.store);
    },
    currentTab() {
      return this.$route.query.tab || "general";
    },
    tabsWithBadge() {
      const baseTabs = [
        { name: "general", displayName: "Général", query: { tab: "general" } },
        { name: "contactInformations", displayName: "Coordonnées", query: { tab: "contactInformations" } },
        { name: "afterSalesService", displayName: "SAV", query: { tab: "afterSalesService" } },
        { name: "billingInformations", displayName: "Facturation", query: { tab: "billingInformations" } },
        { name: "businessVerticals", displayName: "Produits d'assurance", query: { tab: "businessVerticals" } },
        { name: "offers", displayName: "Offres", query: { tab: "offers" } },
        { name: "users", displayName: "Utilisateurs", query: { tab: "users" } },
        ...(this.isStoreOffline ? [{ name: "sellers", displayName: "Vendeurs", query: { tab: "sellers" } }] : []),
        { name: "activity", displayName: "Activités", query: { tab: "activity" } },
        { name: "discount", displayName: "Promotion & challenge", query: { tab: "discount" } },
      ];

      const configMapping = [
        {
          tabName: "general",
          configPath: "configurationGeneralInformations",
          statusKey: "generalInformationsConfigStatus",
        },
        {
          tabName: "contactInformations",
          configPath: "configurationContactInformations",
          statusKey: "contactInformationsConfigStatus",
        },
        {
          tabName: "afterSalesService",
          configPath: "configurationAfterSalesService",
          statusKey: "afterSalesServiceConfigStatus",
        },
      ];

      const badge = {
        text: "!",
        class: "bg-yellow-100 text-yellow-800",
      };

      configMapping.forEach(({ tabName, configPath, statusKey }) => {
        const configStatus = this.store?.[configPath]?.[statusKey];
        if (configStatus === "pending_validation") {
          const tab = baseTabs.find((t) => t.name === tabName);
          if (tab) tab.badge = badge;
        }
      });

      return baseTabs;
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("sellers", ["delete"]),
    ...mapActions("stores", ["updateConfigurationStatus"]),

    emitStoreUpdated() {
      this.$emit("store-updated");
    },
    async deleteSeller(sellerId) {
      const response = await this.delete({ id: sellerId });
      if (!response?.success) return;
      await this.notify({
        category: "simple",
        type: "success",
        title: "Vendeur supprimé",
      });
      this.$emit("store-updated");
    },
    validateConfigurationGeneralInformations() {
      this.updateStoreConfigurationStatus("general_informations", "validated");
    },
    rejectConfigurationGeneralInformations() {
      this.updateStoreConfigurationStatus("general_informations", "rejected");
    },
    validateConfigurationContactInformations() {
      this.updateStoreConfigurationStatus("contact_informations", "validated");
    },
    rejectConfigurationContactInformations() {
      this.updateStoreConfigurationStatus("contact_informations", "rejected");
    },
    validateConfigurationAfterSalesService() {
      this.updateStoreConfigurationStatus("after_sales_service", "validated");
    },
    rejectConfigurationAfterSalesService() {
      this.updateStoreConfigurationStatus("after_sales_service", "rejected");
    },
    async updateStoreConfigurationStatus(step, status) {
      const { success } = await this.updateConfigurationStatus({ storeId: this.storeId, step, status });
      if (!success) return;

      await this.notify({
        category: "simple",
        type: "success",
        title: "Mise à jour effectuée",
      });
      this.$emit("store-updated");
    },
  },
};
</script>
